// The purchase-order page (F-FE-N97) — an xl Sheet mirroring the vendor
// profile. Before this, a completed order's contents were unviewable except
// by opening the Return dialog, Print vanished exactly when an order was out
// with the vendor, and Cancel existed in the API client with no UI at all.
// URL-addressable: ?tab=purchasing&po=<id>.
import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { PURCHASING_STRINGS } from './purchasing/purchasingStrings'
import { Link } from '@tanstack/react-router'
import { Printer, MessageCircle, RotateCcw, Trash2, XCircle } from 'lucide-react'
import apiClient from '@/api/client'
import type { PurchaseOrderDetail } from '@/types'
import { useKitchenFlowEnabled } from '@/hooks/useKitchenFlowEnabled'
import { FetchedTimeline } from '@/components/inventory-flow/DocumentTimeline'
import { PoPhotos } from './purchasing/PoPhotos'
import { useCurrency } from '@/contexts/CurrencyContext'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Sheet, SheetContent, SheetHeader, SheetBody, SheetTitle } from '@/components/ui/sheet'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { cn, formatDateHuman } from '@/lib/utils'
import type { VendorTone } from './vendorStatus'
import { BT } from '@/lib/inventoryFlow/boughtStrings'
import { poRef, poStatusLabel, poStatusTone, poPrimaryAction, PO_PRIMARY_ACTION_LABEL } from './poStatus'

type ToastFn = (type: 'success' | 'error', message: string) => void

const TONE_CLASS: Record<VendorTone, string> = {
  danger: 'bg-danger-surface text-danger-fg border-danger-border',
  warning: 'bg-warning-surface text-warning-fg border-warning-border',
  success: 'bg-success-surface text-success-fg border-success-border',
  info: 'bg-info-surface text-info-fg border-info-border',
  neutral: 'bg-neutral-surface text-neutral-fg border-neutral-border',
}

function Tile({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <Card>
      <CardContent className="flex flex-col gap-1 px-4 py-3.5">
        <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</span>
        <span className="text-lg font-semibold tabular-nums">{value}</span>
        {sub && <span className="text-xs text-muted-foreground">{sub}</span>}
      </CardContent>
    </Card>
  )
}

export function PoDetailSheet({
  poId,
  onClose,
  isAdmin,
  canApprove,
  canApproveOrder,
  showToast,
  onReceive,
  onReturn,
  onSend,
  onApprove,
  onPrint,
}: {
  poId: string | null
  onClose: () => void
  isAdmin: boolean
  /** Kitchen flow lane D: may this user approve? Defaults to isAdmin. */
  canApprove?: boolean
  /** Kitchen flow pilot (Batch 5): may this user approve THIS order? */
  canApproveOrder?: (o: PurchaseOrderDetail) => boolean
  showToast: ToastFn
  onReceive: (id: string) => void
  onReturn: (id: string) => void
  onSend: (detail: PurchaseOrderDetail) => void
  onApprove: (id: string) => void
  onPrint: (detail: PurchaseOrderDetail) => void
}) {
  const qc = useQueryClient()
  const { formatCurrency } = useCurrency()
  const open = !!poId

  const { data: detailRes } = useQuery({
    queryKey: ['purchaseOrder', poId],
    queryFn: () => apiClient.getPurchaseOrder(poId!),
    enabled: open,
  })
  const detail: PurchaseOrderDetail | undefined = detailRes?.data
  const kitchenFlow = useKitchenFlowEnabled()

  const [cancelOpen, setCancelOpen] = useState(false)
  const [closeOpen, setCloseOpen] = useState(false)
  const [closeReason, setCloseReason] = useState('')
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deletePin, setDeletePin] = useState('')
  const [deleteReason, setDeleteReason] = useState('')

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['purchaseOrders'] })
    qc.invalidateQueries({ queryKey: ['purchaseOrder', poId] })
    qc.invalidateQueries({ queryKey: ['inventoryActivity'] })
    // F-FE-N118: Items and Alerts show "Low · N on order" from open orders —
    // cancelling or closing one must take those units off the shelf view.
    qc.invalidateQueries({ queryKey: ['stockItems'] })
    qc.invalidateQueries({ queryKey: ['stockAlerts'] })
  }

  const sendWhatsApp = useMutation({
    mutationFn: (id: string) => apiClient.sendPurchaseOrderWhatsApp(id),
    onSuccess: (res) => {
      const url = res.data?.wa_url
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer')
        showToast('success', 'Opening WhatsApp…')
      } else {
        showToast('error', res.error || 'Could not build WhatsApp link')
      }
    },
    onError: (e: Error) => showToast('error', e.message || 'Failed to send'),
  })

  const cancelPO = useMutation({
    mutationFn: (id: string) => apiClient.cancelPurchaseOrder(id),
    onSuccess: () => {
      invalidate()
      showToast('success', 'Order cancelled')
      setCancelOpen(false)
    },
    onError: (e: Error) => {
      setCancelOpen(false)
      showToast(
        'error',
        e.message.includes('bill')
          ? "This order has a bill for goods already received — settle or void that bill on the vendor's page first."
          : e.message || 'Failed to cancel',
      )
    },
  })

  const closePO = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => apiClient.closePurchaseOrder(id, reason),
    onSuccess: () => {
      invalidate()
      showToast('success', 'Order closed — the remainder is no longer expected')
      setCloseOpen(false)
      setCloseReason('')
    },
    onError: (e: Error) => showToast('error', e.message || 'Failed to close'),
  })

  const deletePO = useMutation({
    mutationFn: (id: string) => apiClient.deletePurchaseOrder(id, { pin: deletePin.trim(), reason: deleteReason.trim() }),
    onSuccess: (res) => {
      invalidate()
      const by = res.data?.authorized_by
      showToast('success', by ? `Order deleted by ${by}` : 'Order deleted')
      setDeleteOpen(false)
      setDeletePin('')
      setDeleteReason('')
      onClose()
    },
    onError: (e: Error) => showToast('error', e.message || 'Failed to delete order'),
  })

  const money = useMemo(() => {
    if (!detail) return null
    const ordered = detail.ordered_value ?? 0
    const received = detail.received_value ?? 0
    const bills = (detail.bills ?? []).filter((b) => b.status !== 'void')
    const billOutstanding = bills.reduce((s, b) => s + b.outstanding, 0)
    const billAmount = bills.reduce((s, b) => s + b.amount, 0)
    const hasDraftBill = bills.some((b) => b.status === 'draft')
    const dueDates = bills.map((b) => b.due_date).filter(Boolean) as string[]
    const nextDue = dueDates.sort()[0]
    return { ordered, received, bills, billOutstanding, billAmount, hasDraftBill, nextDue }
  }, [detail])

  const returnedLines = useMemo(
    () => (detail?.lines ?? []).filter((ln) => (ln.quantity_returned ?? 0) > 0.0001),
    [detail],
  )
  const canReturn = useMemo(
    () => (detail?.lines ?? []).some((ln) => (ln.quantity_returnable ?? 0) > 0.0001 || (ln.quantity_elsewhere ?? 0) > 0.0001),
    [detail],
  )

  const status = detail?.status ?? ''
  const roleMayApprove = canApprove ?? isAdmin
  const mayApproveThis = detail ? (canApproveOrder ? canApproveOrder(detail) : roleMayApprove) : false
  const action = detail ? poPrimaryAction(status, mayApproveThis) : null
  // Self-approval off and this is your own order: say why Approve is missing.
  const ownOrderNote = detail != null && status === 'pending_approval' && roleMayApprove && !mayApproveThis
  const active = status !== 'cancelled'
  const canCancel = ['pending_approval', 'approved', 'ordered', 'partially_received', 'draft'].includes(status)
  const canClose = status === 'ordered' || status === 'partially_received'
  const canDelete = ['pending_approval', 'approved', 'ordered', 'draft'].includes(status)

  return (
    <>
      <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
        <SheetContent size="xl" className="p-0" aria-describedby={undefined}>
          {!detail ? (
            <SheetHeader>
              <SheetTitle className="text-xl">Loading order…</SheetTitle>
            </SheetHeader>
          ) : (
            <>
              <SheetHeader className="gap-3">
                <div className="flex flex-wrap items-start gap-3 pr-8">
                  <SheetTitle className="text-2xl tabular-nums">Order #{poRef(detail.id)}</SheetTitle>
                  <span className={cn('mt-1.5 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium', TONE_CLASS[poStatusTone(status)])}>
                    <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden="true" />
                    {poStatusLabel(status)}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">
                  <Link
                    to="/admin/inventory"
                    search={{ tab: 'vendors' as const, vendor: detail.supplier_id }}
                    className="font-medium text-foreground hover:underline"
                  >
                    {detail.supplier_name}
                  </Link>
                  {' · '}Created {formatDateHuman(detail.created_at)}
                  {detail.expected_date && <> · Expected {formatDateHuman(detail.expected_date)}</>}
                  {detail.receipt_date && status === 'received' && <> · Delivered {formatDateHuman(detail.receipt_date)}</>}
                </p>
                {detail.notes && <p className="text-sm text-muted-foreground">{detail.notes}</p>}
                {/* Kitchen flow lane D — only ever set on a kitchen-flow tenant. */}
                {detail.source_requisition_id && (
                  <p className="rounded-md border border-info-border bg-info-surface/50 px-3 py-2 text-sm font-medium">
                    {PURCHASING_STRINGS.requisition.banner(detail.source_requisition_no ?? null)}
                  </p>
                )}
                {/* F-CTRL-N75: already bought — where it went, who paid, the stall. */}
                {detail.bought_paid_by ? (
                  <p className="rounded-md border px-3 py-2 text-sm">
                    <span className="font-medium">{BT.boughtTitle}</span>
                    {' — '}
                    {BT.payers[detail.bought_paid_by]}
                    {detail.bought_paid_by === 'buyer' && detail.bought_buyer_name ? ` (${BT.toReimburse(detail.bought_buyer_name)})` : ''}
                    {detail.bought_delivered_to ? ` · ${BT.deliverTo} ${BT.places[detail.bought_delivered_to].toLowerCase()}` : ''}
                    {detail.bought_stall ? ` · ${detail.bought_stall}` : ''}
                  </p>
                ) : detail.paid_from === 'petty_cash' && (
                  <p className="rounded-md border px-3 py-2 text-sm">{PURCHASING_STRINGS.paidFrom.cash} — {PURCHASING_STRINGS.paidFrom.cashHelp}</p>
                )}

                {ownOrderNote && <p className="text-sm text-muted-foreground">{PURCHASING_STRINGS.approvers.ownOrder}</p>}
                <div className="flex flex-wrap gap-2">
                  {action === 'approve' && (
                    <Button size="sm" onClick={() => onApprove(detail.id)}>{PO_PRIMARY_ACTION_LABEL.approve}</Button>
                  )}
                  {action === 'send' && (
                    <Button size="sm" onClick={() => onSend(detail)}>{PO_PRIMARY_ACTION_LABEL.send}</Button>
                  )}
                  {action === 'receive' && (
                    <Button size="sm" onClick={() => onReceive(detail.id)}>{PO_PRIMARY_ACTION_LABEL.receive}</Button>
                  )}
                  {/* Print works in EVERY state — the old table hid it once an
                      order was 'ordered', the one moment paper matters most. */}
                  <Button size="sm" variant="outline" onClick={() => onPrint(detail)}>
                    <Printer className="mr-1.5 h-3.5 w-3.5" /> Print
                  </Button>
                  {active && (
                    <Button size="sm" variant="outline" disabled={sendWhatsApp.isPending} onClick={() => sendWhatsApp.mutate(detail.id)}>
                      <MessageCircle className="mr-1.5 h-3.5 w-3.5" /> WhatsApp
                    </Button>
                  )}
                  {canReturn && (
                    <Button size="sm" variant="outline" onClick={() => onReturn(detail.id)}>
                      <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Return stock
                    </Button>
                  )}
                  {canClose && (
                    <Button size="sm" variant="outline" onClick={() => { setCloseOpen(true); setCloseReason('') }}>
                      Close — the rest isn&apos;t coming
                    </Button>
                  )}
                  {canCancel && (
                    <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => setCancelOpen(true)}>
                      <XCircle className="mr-1.5 h-3.5 w-3.5" /> Cancel order
                    </Button>
                  )}
                  {canDelete && (
                    <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => setDeleteOpen(true)}>
                      <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Delete
                    </Button>
                  )}
                </div>
              </SheetHeader>

              <SheetBody className="flex flex-col gap-6">
                {/* Kitchen flow: the delivery-slip photos taken at receiving. */}
                {kitchenFlow && <PoPhotos poId={detail.id} poStatus={detail.status} showToast={showToast} />}
                {money && (money.ordered > 0 || money.received > 0 || money.bills.length > 0) && (
                  <div className="grid grid-cols-2 gap-2.5 lg:grid-cols-4">
                    <Tile label="Order value" value={money.ordered > 0 ? formatCurrency(money.ordered) : '—'} />
                    <Tile label="Received so far" value={money.received > 0 ? formatCurrency(money.received) : '—'} />
                    {status !== 'received' && status !== 'cancelled' && money.ordered > money.received && (
                      <Tile label="Still to come" value={formatCurrency(money.ordered - money.received)} sub="at line costs" />
                    )}
                    {money.bills.length > 0 && (
                      <Link
                        to="/admin/inventory"
                        search={{ tab: 'vendors' as const, vendor: detail.supplier_id }}
                        className="focus-visible:outline-none"
                      >
                        <Card className="h-full cursor-pointer transition-colors hover:border-primary/50">
                          <CardContent className="flex flex-col gap-1 px-4 py-3.5">
                            <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">The bill</span>
                            <span className="text-lg font-semibold tabular-nums">
                              {money.billOutstanding > 0.01 ? formatCurrency(money.billOutstanding) : 'Paid in full'}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {money.billOutstanding > 0.01
                                ? money.hasDraftBill
                                  ? 'drafted — confirm against their invoice'
                                  : money.nextDue
                                    ? `you owe · due ${formatDateHuman(money.nextDue)}`
                                    : 'you owe'
                                : `${formatCurrency(money.billAmount)} settled`}
                            </span>
                          </CardContent>
                        </Card>
                      </Link>
                    )}
                  </div>
                )}

                <section className="space-y-2">
                  <h3 className="text-sm font-semibold">What you ordered</h3>
                  <div className="overflow-hidden rounded-lg border">
                    <ul className="divide-y">
                      {detail.lines.map((ln) => {
                        const short = status === 'received' && ln.quantity_received < ln.quantity_ordered - 0.0001
                        return (
                          <li key={ln.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5 text-sm">
                            <span className="min-w-0 flex-1 basis-40 font-medium">{ln.item_name}</span>
                            <span className="tabular-nums text-muted-foreground">
                              {ln.quantity_received > 0 && ln.quantity_received < ln.quantity_ordered - 0.0001
                                ? `${ln.quantity_received} of ${ln.quantity_ordered} ${ln.unit}`
                                : `${ln.quantity_ordered} ${ln.unit}`}
                              {(ln.quantity_returned ?? 0) > 0.0001 && (
                                <span className="text-warning-fg"> · {ln.quantity_returned} returned</span>
                              )}
                              {short && <span className="text-warning-fg"> · short</span>}
                            </span>
                            <span className="w-28 text-right tabular-nums">
                              {ln.unit_cost != null ? formatCurrency(ln.quantity_ordered * ln.unit_cost) : '—'}
                            </span>
                          </li>
                        )
                      })}
                    </ul>
                  </div>
                </section>

                {(detail.receipts ?? []).length > 0 && (
                  <section className="space-y-2">
                    <h3 className="text-sm font-semibold">Deliveries</h3>
                    <ul className="space-y-1.5">
                      {(detail.receipts ?? []).map((r) => (
                        <li key={r.movement_id} className="flex flex-wrap items-baseline gap-x-3 text-sm">
                          <span className="tabular-nums text-muted-foreground">{formatDateHuman(r.created_at)}</span>
                          <span>
                            Received {r.quantity} {r.unit} {r.item_name}
                            {r.total_cost != null && r.total_cost > 0 && (
                              <span className="text-muted-foreground"> · {formatCurrency(r.total_cost)}</span>
                            )}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}

                {returnedLines.length > 0 && (
                  <section className="space-y-2">
                    <h3 className="text-sm font-semibold">Returns</h3>
                    <ul className="space-y-1.5">
                      {returnedLines.map((ln) => (
                        <li key={ln.id} className="text-sm">
                          Sent back {ln.quantity_returned} {ln.unit} {ln.item_name}
                        </li>
                      ))}
                    </ul>
                  </section>
                )}
                {/* Kitchen flow Stage 3: the order's history, with who did each step. */}
                {kitchenFlow && <FetchedTimeline kind="po" id={detail.id} />}
              </SheetBody>
            </>
          )}
        </SheetContent>
      </Sheet>

      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl">Cancel this order?</DialogTitle>
            <DialogDescription>
              The vendor won&apos;t be expected to deliver it, and it moves to History. Stock already
              received stays received.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setCancelOpen(false)}>Keep the order</Button>
            <Button variant="destructive" disabled={cancelPO.isPending} onClick={() => poId && cancelPO.mutate(poId)}>
              {cancelPO.isPending ? 'Cancelling…' : 'Cancel order'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={closeOpen} onOpenChange={(o) => !o && setCloseOpen(false)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl">Close this order?</DialogTitle>
            <DialogDescription>
              Stops expecting the rest of the delivery. What you already received stays on the books;
              raise a fresh order when you reorder.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-1">
            <Label htmlFor="po-close-reason">Reason (optional)</Label>
            <Textarea
              id="po-close-reason"
              className="min-h-[88px] resize-y"
              value={closeReason}
              onChange={(e) => setCloseReason(e.target.value)}
              placeholder="e.g. vendor short-shipped; remainder won't arrive"
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setCloseOpen(false)}>Cancel</Button>
            <Button disabled={closePO.isPending} onClick={() => poId && closePO.mutate({ id: poId, reason: closeReason.trim() })}>
              {closePO.isPending ? 'Closing…' : 'Close order'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteOpen} onOpenChange={(o) => !o && !deletePO.isPending && setDeleteOpen(false)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl">Delete order</DialogTitle>
            <DialogDescription>
              Permanently removes an order nothing has been received against. Needs an admin or
              manager PIN and a reason.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <div className="space-y-2">
              <Label htmlFor="po-delete-reason">Reason</Label>
              <Textarea
                id="po-delete-reason"
                className="min-h-24"
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                placeholder="Example: Duplicate order created by mistake"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="po-delete-pin">Admin / manager PIN</Label>
              <Input
                id="po-delete-pin"
                type="password"
                inputMode="numeric"
                maxLength={4}
                className="tracking-[0.4em]"
                value={deletePin}
                onChange={(e) => setDeletePin(e.target.value.replace(/\D/g, '').slice(0, 4))}
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" disabled={deletePO.isPending} onClick={() => setDeleteOpen(false)}>Cancel</Button>
            <Button
              variant="destructive"
              disabled={deletePin.length !== 4 || !deleteReason.trim() || deletePO.isPending}
              onClick={() => poId && deletePO.mutate(poId)}
            >
              {deletePO.isPending ? 'Deleting…' : 'Delete order'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
